import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatPrice } from "@/lib/currency";
import { api } from "@/lib/api";
import { FEATURE_OPTIONS } from "@/components/oakbridge/Calculator";
import { Check, Loader2 } from "lucide-react";

export default function InquiryForm({ currency, rates, selectedFeatures, estimateGbp }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const featureLabels = FEATURE_OPTIONS.filter((f) => selectedFeatures.includes(f.id)).map(
    (f) => f.label
  );

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = "Please tell us your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = "We need a valid email to reply.";
    if (phone.trim() && !/^[+()\d\s\-.]{6,}$/.test(phone.trim())) {
      e.phone = "Please enter a valid phone number, or leave it blank.";
    }
    if (!details.trim() || details.trim().length < 10) e.details = "A couple of sentences please.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        project_details: details.trim(),
        features: featureLabels,
        estimated_total_gbp: estimateGbp,
        currency,
      });
      setSubmitted(true);
      toast.success("Got it — we’ll be in touch within a day.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="inquiry" data-testid="inquiry-section" className="border-t border-[#E5E0D8]">
        <div className="oak-container oak-section">
          <div
            data-testid="inquiry-success"
            className="max-w-2xl mx-auto rounded-2xl border border-[#E5E0D8] bg-white p-10 md:p-14 text-center shadow-sm"
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-[#2B5C40] text-white flex items-center justify-center">
              <Check className="w-6 h-6" />
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mt-6 text-[#1A3622]">
              Thanks, {name.split(" ")[0]} — message received.
            </h2>
            <p className="mt-4 text-[17px] text-[#5C665F]">
              We’ll get back to you at <strong className="text-[#1C211E]">{email}</strong> within
              one working day with a fixed quote and a few quick questions.
            </p>
            <p className="mt-2 text-sm text-[#5C665F]">No deposits taken at this stage.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="inquiry" data-testid="inquiry-section" className="border-t border-[#E5E0D8]">
      <div className="oak-container oak-section">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-start">
          <div>
            <p className="text-sm uppercase tracking-[0.18em] text-[#5C665F]">Start a project</p>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight mt-3 text-[#1A3622]">
              Tell us what you’re after.
            </h2>
            <p className="mt-5 text-[17px] leading-relaxed text-[#5C665F] max-w-md">
              No deposit required to get a quote. We’ll reply within one working day with a fixed
              price, a rough timeline, and any clarifying questions.
            </p>

            <div className="mt-8 rounded-xl border border-[#E5E0D8] bg-[#FAF9F6] p-5">
              <p className="text-xs uppercase tracking-[0.16em] text-[#5C665F]">Your estimate so far</p>
              <p
                data-testid="inquiry-estimate"
                className="font-heading font-extrabold text-3xl md:text-4xl text-[#1A3622] mt-2"
              >
                {formatPrice(estimateGbp, currency, rates)}
              </p>
              {featureLabels.length > 0 ? (
                <ul className="mt-3 flex flex-wrap gap-2">
                  {featureLabels.map((f) => (
                    <li
                      key={f}
                      className="rounded-full bg-white border border-[#E5E0D8] px-3 py-1 text-[12px] text-[#1C211E]"
                    >
                      {f}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm text-[#5C665F]">
                  Tick features in the calculator above and they’ll appear here.
                </p>
              )}
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="inquiry-form"
            className="rounded-2xl border border-[#E5E0D8] bg-white p-7 md:p-9 shadow-sm space-y-5"
            noValidate
          >
            <div>
              <Label htmlFor="name" className="text-[#1C211E] font-medium">
                Your name
              </Label>
              <Input
                id="name"
                data-testid="inquiry-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Smith"
                className="mt-2 h-11 border-[#E5E0D8] bg-white focus-visible:ring-[#C17F59] focus-visible:ring-offset-1"
              />
              {errors.name && (
                <p data-testid="error-name" className="mt-1 text-sm text-red-700">
                  {errors.name}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="email" className="text-[#1C211E] font-medium">
                Email address
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="inquiry-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@yourbusiness.com"
                className="mt-2 h-11 border-[#E5E0D8] bg-white focus-visible:ring-[#C17F59] focus-visible:ring-offset-1"
              />
              {errors.email && (
                <p data-testid="error-email" className="mt-1 text-sm text-red-700">
                  {errors.email}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="phone" className="text-[#1C211E] font-medium">
                Phone number <span className="text-[#5C665F] font-normal">(optional)</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                data-testid="inquiry-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+44 7700 900123"
                autoComplete="tel"
                className="mt-2 h-11 border-[#E5E0D8] bg-white focus-visible:ring-[#C17F59] focus-visible:ring-offset-1"
              />
              {errors.phone && (
                <p data-testid="error-phone" className="mt-1 text-sm text-red-700">
                  {errors.phone}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="details" className="text-[#1C211E] font-medium">
                Project details
              </Label>
              <Textarea
                id="details"
                data-testid="inquiry-details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="A few bullet points about what you want, who it’s for, and when you’d like it live."
                rows={6}
                className="mt-2 border-[#E5E0D8] bg-white focus-visible:ring-[#C17F59] focus-visible:ring-offset-1"
              />
              {errors.details && (
                <p data-testid="error-details" className="mt-1 text-sm text-red-700">
                  {errors.details}
                </p>
              )}
            </div>

            <button
              type="submit"
              data-testid="submit-inquiry-btn"
              disabled={submitting}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1A3622] px-6 h-12 text-base font-medium text-white transition-all hover:-translate-y-0.5 hover:shadow-lg hover:bg-[#122618] disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                </>
              ) : (
                <>Send my inquiry</>
              )}
            </button>
            <p className="text-[12px] text-[#5C665F] text-center">
              By sending you agree to be contacted about your project. No mailing list, ever.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
